import * as Constants from '@labradorsports/constants';
import SportConfigs from '@labradorsports/sports';

import FlagFullFieldBackdrop from './field-backdrop/flag-full-field-backdrop.js';
import HighSchoolFullFieldBackdrop from './field-backdrop/highschool-full-field-backdrop.js';

const { Sites } = Constants;
const { Sports, PlayerPositions } = SportConfigs[Sites.footballlab];

const PlayTypes = {
    HALF_FIELD: 'HALF_FIELD',
    FULL_FIELD: 'FULL_FIELD',
    DRILL: 'DRILL',
};

const FieldTypes = {
    HALF_FIELD: 'HALF_FIELD',
    FULL_FIELD: 'FULL_FIELD',
    CUSTOM: 'CUSTOM',
};

const FieldBackdropMap = {
    [Sports.Flag]: FlagFullFieldBackdrop,
    [Sports.Passing]: FlagFullFieldBackdrop,
    [Sports.Tackle]: HighSchoolFullFieldBackdrop,
};

const FieldDescriptor = 'Field';

const GoalDesign = {};

const Formations = {
    StartingTackle: 'StartingTackle',
    Starting5v5: 'Starting5v5',
    Starting7v7: 'Starting7v7',
    StartingTackleDefense: 'StartingTackleDefense',
    Starting5v5Defense: 'Starting5v5Defense',
    Starting7v7Defense: 'Starting7v7Defense',
};

const ToolTypes = {
    ...Constants.ToolTypes,
    BLOCK: 'BLOCK',
    GROUNDBALL: 'GROUNDBALL',
    PRESNAP: 'PRESNAP',
    SNAP: 'SNAP',
    HANDOFF: 'HANDOFF',
    ROUTE_GO: 'ROUTE_GO',
    ROUTE_POST: 'ROUTE_POST',
    ROUTE_CORNER: 'ROUTE_CORNER',
    ROUTE_DIG: 'ROUTE_DIG',
    ROUTE_OUT: 'ROUTE_OUT',
    ROUTE_CURL: 'ROUTE_CURL',
    ROUTE_COMEBACK: 'ROUTE_COMEBACK',

    ...Formations,
};

const FormationDefinitions = {
    [Formations.StartingTackle]: {
        name: 'Tackle',
        sport: Sports.Tackle,
        anchorType: 'CENTER',
        attributes: ['pro-style', 'balanced', 'traditional', 'intermediate', 'standard-personnel', 'single-back'],
        description:
            'A classic pro-style formation with a balanced offensive line, single running back, and multiple receiver options. This versatile formation allows for both run and pass plays while maintaining good protection for the quarterback and creating multiple offensive threats.',
        fieldPieces: [],
        balls: [],
        version: 1,
        initial: [
            { origin: { x: -3, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.OFFENSIVE_TACKLE },
            { origin: { x: -1.5, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.OFFENSIVE_GUARD },
            { origin: { x: 0, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.CENTER },
            { origin: { x: 1.5, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.OFFENSIVE_GUARD },
            { origin: { x: 3, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.OFFENSIVE_TACKLE },

            { origin: { x: 0, y: 3 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.QUARTERBACK },

            { origin: { x: -5, y: 6 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.WIDE_RECEIVER },
            { origin: { x: -2.5, y: 6 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.RUNNING_BACK },
            { origin: { x: 0, y: 6 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.TIGHT_END },
            { origin: { x: 2.5, y: 6 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.RUNNING_BACK },
            { origin: { x: 5, y: 6 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.WIDE_RECEIVER },
        ],
    },
    [Formations.Starting5v5]: {
        name: '5v5',
        sport: Sports.Flag,
        anchorType: 'CENTER',
        attributes: ['pro-style', 'balanced', 'traditional', 'intermediate', 'standard-personnel', 'single-back'],
        description:
            'A classic pro-style formation with a balanced offensive line, single running back, and multiple receiver options. This versatile formation allows for both run and pass plays while maintaining good protection for the quarterback and creating multiple offensive threats.',
        fieldPieces: [],
        balls: [],
        version: 1,
        initial: [
            { origin: { x: 2, y: 2.5 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.RUNNING_BACK },
            { origin: { x: 0, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.CENTER },

            { origin: { x: 0, y: 2.5 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.QUARTERBACK },

            { origin: { x: -7.5, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.WIDE_RECEIVER },
            { origin: { x: 7.5, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.WIDE_RECEIVER },
        ],
    },
    [Formations.Starting7v7]: {
        name: '7v7',
        sport: Sports.Passing,
        anchorType: 'CENTER',
        attributes: ['pro-style', 'balanced', 'traditional', 'intermediate', 'standard-personnel', 'single-back'],
        description:
            'A classic pro-style formation with a balanced offensive line, single running back, and multiple receiver options. This versatile formation allows for both run and pass plays while maintaining good protection for the quarterback and creating multiple offensive threats.',
        fieldPieces: [],
        balls: [],
        version: 1,
        initial: [
            { origin: { x: 1.75, y: 1.5 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.RUNNING_BACK },
            { origin: { x: 3, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.TIGHT_END },
            { origin: { x: 0, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.CENTER },

            { origin: { x: 0, y: 1.5 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.QUARTERBACK },

            { origin: { x: -6, y: 0.75 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.WIDE_RECEIVER },
            { origin: { x: -9, y: 0 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.WIDE_RECEIVER },
            { origin: { x: 9, y: 0.75 }, type: ToolTypes.BLUEPLAYER, role: PlayerPositions.WIDE_RECEIVER },
        ],
    },
    [Formations.StartingTackleDefense]: {
        name: '4-3 Defense',
        sport: Sports.Tackle,
        anchorType: 'CENTER',
        attributes: [Constants.PlaySide.DEFENSE, '4-3', 'balanced', 'traditional', 'base'],
        description:
            'A base 4-3 defense with four down linemen, three linebackers, two cornerbacks, and two safeties. Balanced against both the run and the pass.',
        fieldPieces: [],
        balls: [],
        version: 1,
        initial: [
            { origin: { x: -4, y: -1 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.DEFENSIVE_END },
            { origin: { x: -1.25, y: -1 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.DEFENSIVE_TACKLE },
            { origin: { x: 1.25, y: -1 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.DEFENSIVE_TACKLE },
            { origin: { x: 4, y: -1 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.DEFENSIVE_END },
            { origin: { x: -5, y: -5 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.LINEBACKER },
            { origin: { x: 0, y: -5 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.LINEBACKER },
            { origin: { x: 5, y: -5 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.LINEBACKER },
            { origin: { x: -12, y: -6 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.CORNERBACK },
            { origin: { x: 12, y: -6 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.CORNERBACK },
            { origin: { x: -4, y: -12 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.SAFETY },
            { origin: { x: 4, y: -12 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.SAFETY },
        ],
    },
    [Formations.Starting5v5Defense]: {
        name: '5v5 Defense',
        sport: Sports.Flag,
        anchorType: 'CENTER',
        attributes: [Constants.PlaySide.DEFENSE, 'zone', 'balanced', 'standard'],
        description:
            'A balanced zone look for 5v5 with one rusher or linebacker over the middle, two cornerbacks covering the sidelines, and two safeties deep.',
        fieldPieces: [],
        balls: [],
        version: 1,
        initial: [
            { origin: { x: 0, y: -5 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.LINEBACKER },
            { origin: { x: -8, y: -5 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.CORNERBACK },
            { origin: { x: 8, y: -5 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.CORNERBACK },
            { origin: { x: -4, y: -12 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.SAFETY },
            { origin: { x: 4, y: -12 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.SAFETY },
        ],
    },
    [Formations.Starting7v7Defense]: {
        name: '7v7 Defense',
        sport: Sports.Passing,
        anchorType: 'CENTER',
        attributes: [Constants.PlaySide.DEFENSE, 'zone', 'balanced', 'standard'],
        description:
            'A balanced zone look for 7v7 with three underneath linebackers, two cornerbacks on the outside, and two deep safeties.',
        fieldPieces: [],
        balls: [],
        version: 1,
        initial: [
            { origin: { x: -4, y: -4 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.LINEBACKER },
            { origin: { x: 0, y: -4 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.LINEBACKER },
            { origin: { x: 4, y: -4 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.LINEBACKER },
            { origin: { x: -10, y: -5 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.CORNERBACK },
            { origin: { x: 10, y: -5 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.CORNERBACK },
            { origin: { x: -5, y: -12 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.SAFETY },
            { origin: { x: 5, y: -12 }, type: ToolTypes.ORANGEPLAYER, role: PlayerPositions.SAFETY },
        ],
    },
};

// Dimensions in yards
const highSchoolFieldSettings = {
    width: 53,
    height: 120,
    padding: 3,
    sideline: {},
    center: {
        x: 26.5,
        y: 60,
    },
    goals: {
        [FieldTypes.HALF_FIELD]: [],
        [FieldTypes.FULL_FIELD]: [],
    },
    maxZoomFactor: 6,
    playerScaleZoomFactor: 4,
};

const flagFieldSettings = {
    width: 30,
    height: 70,
    padding: 3,
    sideline: {},
    center: {
        x: 15,
        y: 35,
    },
    goals: {
        [FieldTypes.HALF_FIELD]: [],
        [FieldTypes.FULL_FIELD]: [],
    },
    maxZoomFactor: 6,
    playerScaleZoomFactor: 4,
};

const FieldSettings: DefaultFieldConfig = {
    [Sports.Flag]: flagFieldSettings,
    [Sports.Passing]: flagFieldSettings,
    [Sports.Tackle]: highSchoolFieldSettings,
};

const FeatureFlags = {
    EnableGoalie: false,
    EnableCustomField: true,
    OnionSkin: true,
};

const NewPlaySettings = {
    FrameCount: 3,
    StartingFormation: {
        [Sports.Tackle]: Formations.StartingTackle,
        [Sports.Flag]: Formations.Starting5v5,
        [Sports.Passing]: Formations.Starting7v7,
    },
    StartingDefenseFormation: {
        [Sports.Tackle]: Formations.StartingTackleDefense,
        [Sports.Flag]: Formations.Starting5v5Defense,
        [Sports.Passing]: Formations.Starting7v7Defense,
    },
    InitialBalls: [
        {
            role: PlayerPositions.CENTER,
        },
    ],
    ViewOptions: {
        onionSkin: {
            framesBack: 2,
            framesForward: 2,
            opacityDelta: 0,
        },
    },
};

const FrameRoles = {};

const MoveConfig: MoveConfigType = {
    ...Constants.MoveConfig,
    [ToolTypes.BLOCK]: {
        baseType: Constants.ToolBaseTypes.MOVE,
        targets: [ToolTypes.BLUEPLAYER],
        head: 'line',
    },
    [ToolTypes.GROUNDBALL]: {
        baseType: Constants.ToolBaseTypes.PASS,
        targets: [ToolTypes.BLUEPLAYER, ToolTypes.ORANGEPLAYER],
        strokeDashArray: '2, 4',
        shorten: 12,
    },
    [ToolTypes.PRESNAP]: {
        baseType: Constants.ToolBaseTypes.MOVE,
        targets: [ToolTypes.BLUEPLAYER, ToolTypes.ORANGEPLAYER],
        pattern: 'WAVE',
    },
    [ToolTypes.SNAP]: {
        baseType: Constants.ToolBaseTypes.PASS,
        targets: [ToolTypes.BLUEPLAYER],
        strokeDashArray: '2, 2',
    },
    [ToolTypes.HANDOFF]: {
        baseType: Constants.ToolBaseTypes.PASS,
        targets: [ToolTypes.BLUEPLAYER],
        strokeDashArray: '2, 2',
    },
};

const RoleDisplay = {
    [PlayerPositions.CENTER]: 'C',
    [PlayerPositions.QUARTERBACK]: 'QB',
    [PlayerPositions.RUNNING_BACK]: 'RB',
    [PlayerPositions.TIGHT_END]: 'TE',
    [PlayerPositions.WIDE_RECEIVER]: 'WR',
    [PlayerPositions.OFFENSIVE_LINEMAN]: 'OL',
    [PlayerPositions.DEFENSIVE_LINEMAN]: 'DL',
    [PlayerPositions.LINEBACKER]: 'LB',
    [PlayerPositions.OFFENSIVE_TACKLE]: 'OT',
    [PlayerPositions.OFFENSIVE_GUARD]: 'OG',
    [PlayerPositions.SAFETY]: 'S',
    [PlayerPositions.CORNERBACK]: 'CB',
    [PlayerPositions.DEFENSIVE_END]: 'DE',
    [PlayerPositions.DEFENSIVE_TACKLE]: 'DT',
    [PlayerPositions.KICKER]: 'K',
    [PlayerPositions.PUNTER]: 'P',
    [PlayerPositions.DEFENSIVE_BACK]: 'DB',
};

const FrameRoleNames = {
    [ToolTypes.ORANGEPLAYER]: {},
};

const FieldLines = {
    GOAL: 'Goal',
    FOOTBALL: 'Football',
};

const PlayTheme: PlayThemeSpec = {
    Players: {
        [PlayerPositions.CENTER]: {
            fill: '#5592C2',
            shape: Constants.PlayerShapes.RECT,
        },
        [PlayerPositions.OFFENSIVE_TACKLE]: {
            fill: '#5592C2',
        },
        [PlayerPositions.OFFENSIVE_GUARD]: {
            fill: '#5592C2',
        },
    },
    Balls: {
        fill: '#C4682A',
        shape: Constants.BallShapes.FOOTBALL,
    },
};

const PlayConfig: PlayConfigSpec = {
    Site: Sites.footballlab,
    PlayTypes,
    FieldTypes,
    FieldDescriptor,
    GoalDesign,
    FieldSettings,
    FeatureFlags,
    NewPlaySettings,
    ToolTypes,
    MoveConfig,
    FrameRoles,
    FrameRoleNames,
    RoleDisplay,
    FieldLines,
    Formations,
    FieldBackdropMap,
    MovementSpeed: 3,
    FormationDefinitions,
    PlayTheme,
};

export default PlayConfig;
